import { Input, Text } from '@vkontakte/vkui';
import classNames from 'clsx';
import React, {
    type ChangeEvent,
    type FormEvent,
    type ReactNode,
    forwardRef,
    useCallback,
    useImperativeHandle,
    useRef,
    useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { emitAnalyticEvent } from 'reactApp/appHelpers/experimentAnalytic';
import { AnalyticEventNames } from 'reactApp/appHelpers/experimentAnalytic/eventNames';
import { EnvironmentSelectors } from 'reactApp/modules/environment/environment';
import { openTariffBuy } from 'reactApp/modules/payment/payment.module';
import type { Product } from 'reactApp/types/Billing';
import { Button } from 'reactApp/ui/Button/Button';
import { normalizeValue, validateValue } from 'reactApp/ui/FlexibleQuotaModal/FlexibleQuota.helpers';
import { closeFlexibleQuotaModal } from 'reactApp/ui/FlexibleQuotaModal/FlexibleQuotaModal.helpers';
import { FloatingTooltip } from 'reactApp/ui/FloatingTooltip/FloatingTooltip';
import { ETooltipPlacement } from 'reactApp/ui/FloatingTooltip/FloatingTooltip.types';
import { ECategoryGa, sendPaymentGa } from 'reactApp/utils/paymentGa';
import { getPriceStringWithPeriod } from 'reactApp/utils/priceHelpers';

import styles from './FlexibleQuotaFooter.css';

export interface FlexibleQuotaFooterHandler {
    setValue: (value: number) => void;
}

interface FlexibleQuotaFooterProps {
    source?: string;
    product?: Product;
    getStep: (value?: number) => number;
    minSpace?: number;
    maxSpace: number;
    onChange?(value?: number): void;
}

export const FlexibleQuotaFooter = forwardRef<FlexibleQuotaFooterHandler, FlexibleQuotaFooterProps>(
    ({ product, minSpace, maxSpace, onChange, getStep, source = '' }, ref) => {
        const priceString = product ? `за ${getPriceStringWithPeriod(product)}` : '';
        const space = product?.space.space || minSpace;
        const units = 'ГБ';
        const dispatch = useDispatch();
        const inputRef = useRef<HTMLInputElement>(null);
        const validationRef = useRef(false);
        const validationHistoryRef = useRef<string[]>([]);
        const [validationMessage, setValidationMessage] = useState<ReactNode | string | null>(null);
        const [value, setValue] = useState(space);
        const isMobile = useSelector(EnvironmentSelectors.isMobile);
        const step = getStep(value);
        useImperativeHandle(
            ref,
            () => ({
                setValue,
            }),
            []
        );

        const handleChangeValue = useCallback((e: ChangeEvent<HTMLInputElement>) => {
            setValue(e.target.value ? Number(e.target.value) : undefined);
        }, []);

        const raiseChange = useCallback(() => {
            if (inputRef.current?.checkValidity()) {
                setValidationMessage(null);
                if (value !== space) {
                    onChange?.(value);
                }
            }
        }, [value, space]);

        const onKeyUp = useCallback(
            (e: React.KeyboardEvent<HTMLInputElement>) => {
                if (e.key === 'Enter') {
                    raiseChange();
                }
            },
            [raiseChange]
        );

        const checkValidity = useCallback(() => {
            const [message, reason] = validateValue(value, step, minSpace, maxSpace);
            const messageWasShown = reason && validationHistoryRef.current.includes(reason);
            if (reason && !messageWasShown) {
                validationHistoryRef.current.push(reason);
            }
            return [validationRef.current && messageWasShown, message];
        }, [value, step, minSpace, maxSpace]);

        const onInvalid = useCallback(
            (e: FormEvent<HTMLInputElement>) => {
                e.preventDefault();
                const [valid, message] = checkValidity();
                if (!valid) {
                    setValidationMessage(message);
                } else {
                    const normalizedValue = normalizeValue(value, minSpace, maxSpace);
                    setValue(normalizedValue);
                    onChange?.(normalizedValue);
                }
            },
            [value, step, minSpace, maxSpace, checkValidity]
        );

        const onCloseTooltip = useCallback(() => {
            validationRef.current = true;
            setValidationMessage(null);
        }, []);

        const onContinue = useCallback(() => {
            if (product) {
                dispatch(
                    openTariffBuy({
                        productId: product.id,
                        paySource: source,
                        isMobile,
                        onSuccess() {
                            closeFlexibleQuotaModal();
                        },
                    })
                );
                emitAnalyticEvent(AnalyticEventNames.FLEXIBLE_QUOTA_MODAL_CLICK, { source });
                sendPaymentGa({
                    action: 'click',
                    eventCategory: ECategoryGa.modal,
                    name: `mnz_${source}`,
                });
            }
        }, [product, source, isMobile]);

        return (
            <div className={styles.root}>
                <div className={styles.infoBlock}>
                    <Text className={styles.text}>Подключить</Text>
                    <Input
                        getRef={inputRef}
                        className={classNames(styles.input, styles.text)}
                        mode="plain"
                        type="number"
                        min={step === 10 ? minSpace : 0} // 0 а не minSpace - хак для вычисляемого step
                        max={maxSpace}
                        step={step}
                        value={value}
                        onChange={handleChangeValue}
                        onInvalid={onInvalid}
                        onBlur={raiseChange}
                        onKeyUp={onKeyUp}
                    />

                    {validationMessage && (
                        <FloatingTooltip
                            contentClassName={styles.tooltipContent}
                            closeOnOutsideClick={false}
                            onClose={onCloseTooltip}
                            target={inputRef}
                            placement={ETooltipPlacement.bottom}
                            text={validationMessage}
                        />
                    )}
                    <Text className={styles.text}>
                        {units} {priceString}
                    </Text>
                </div>
                <Button type="button" theme="vk" primary className={styles.button} disabled={value === 0} onClick={onContinue}>
                    Продолжить
                </Button>
            </div>
        );
    }
);

FlexibleQuotaFooter.displayName = 'FlexibleQuotaFooter';
